import { PerformanceMetric, usePerformance } from '@/context/performance';
import { recordRumMilestone, RumMilestone } from '@/services/rum';
import { setChildAppLoaded } from '@customerio/hydra-nav-state';
import { useCallback, useRef, useState } from 'react';
import { AppBundle, getImportShim } from '../utils/app-bundles';
import { PageLoader } from '@customerio/pluma-components/react';
import { AppBundleLoader } from '@/components/app-bundle-loader';
import {
  plumaColorSchemePreferenceStore,
  plumaColorSchemeStore,
  plumaContextValueStore,
  plumaThemeStore,
  plumaThemesStore,
} from '@/components/connected-pluma-provider';
import { useMountEffect } from '@/hooks/state/use-mount-effect';

// Types for dynamically loaded Design Studio app
type DesignStudioRoot = {
  render: (element: React.ReactElement) => void;
  unmount: () => void;
};

type DesignStudioReactDOMClient = {
  createRoot: (container: Element, options?: { identifierPrefix?: string }) => DesignStudioRoot;
};

type DesignStudioAppComponent = React.ComponentType<{
  basename?: string;
  plumaContextValue: typeof plumaContextValueStore;
  plumaTheme: typeof plumaThemeStore;
  plumaColorScheme: typeof plumaColorSchemeStore;
  plumaColorSchemePreference: typeof plumaColorSchemePreferenceStore;
  plumaThemes: typeof plumaThemesStore;
}>;

type DesignStudioReact = {
  createElement: (type: unknown, props: Record<string, unknown> | null, ...children: unknown[]) => unknown;
};

type DesignStudioModule = {
  rootComponent: DesignStudioAppComponent;
  ReactDOMClient: DesignStudioReactDOMClient;
  // React 18 instance from the embedded app — used to create elements so that
  // the React 18 reconciler receives a compatible $$typeof symbol
  // (Symbol.for('react.element') vs React 19's 'react.transitional.element').
  React: DesignStudioReact;
};

export default function DesignStudio(props?: { basename?: string }) {
  return <DesignStudioBackgroundApp basename={props?.basename} />;
}

export function DesignStudioNonBackgroundApp(props?: { basename?: string }) {
  const ref = useRef(null);
  const rendered = useRef(false);
  const root = useRef<DesignStudioRoot | null>(null);
  const [loaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const recordPerformanceMetric = usePerformance();

  if (loadError != null) {
    throw loadError;
  }

  useMountEffect(() => {
    setChildAppLoaded(false);
  });

  useMountEffect(() => {
    const load = async () => {
      recordPerformanceMetric(PerformanceMetric.ChildAppLoadStart);
      // Direct call: the legacy provider is a no-op on embed routes.
      recordRumMilestone(RumMilestone.ChildAppLoadStart);
      const {
        rootComponent: DesignStudioApp,
        ReactDOMClient: DesignStudioReactDomClient,
        React: DesignStudioReact,
      } = (await getImportShim()(AppBundle.DesignStudio)) as DesignStudioModule;
      setIsLoaded(true);

      if (ref.current == null) {
        return;
      }

      root.current ??= DesignStudioReactDomClient.createRoot(ref.current, { identifierPrefix: 'design_studio_' });

      if (!rendered.current && root.current != null) {
        root.current.render(
          DesignStudioReact.createElement(DesignStudioApp, {
            basename: props?.basename,
            plumaContextValue: plumaContextValueStore,
            plumaTheme: plumaThemeStore,
            plumaColorScheme: plumaColorSchemeStore,
            plumaColorSchemePreference: plumaColorSchemePreferenceStore,
            plumaThemes: plumaThemesStore,
          }) as React.ReactElement,
        );
        rendered.current = true;
        recordRumMilestone(RumMilestone.ChildAppRendered);
      }
    };

    load().catch((error: unknown) => {
      setLoadError(error instanceof Error ? error : new Error('Failed to load Design Studio.'));
    });

    return () => {
      if (rendered.current != null) {
        root.current?.unmount();
        root.current = null;

        rendered.current = false;
        setIsLoaded(false);
      }
    };
  });

  return (
    <>
      <div ref={ref} style={{ display: loaded ? 'flex' : 'none', minWidth: 0, flex: 1 }} />
      {loaded ? null : <PageLoader />}
    </>
  );
}

function DesignStudioBackgroundApp(props?: { basename?: string }) {
  const rendered = useRef(false);
  const root = useRef<DesignStudioRoot | null>(null);
  const renderDesignStudioApp = useCallback(
    async (appWrapper: HTMLDivElement) => {
      recordRumMilestone(RumMilestone.ChildAppLoadStart);
      const {
        rootComponent: DesignStudioApp,
        ReactDOMClient: DesignStudioReactDomClient,
        React: DesignStudioReact,
      } = (await getImportShim()(AppBundle.DesignStudio)) as DesignStudioModule;

      root.current ??= DesignStudioReactDomClient.createRoot(appWrapper, { identifierPrefix: 'design_studio_' });

      if (!rendered.current && root.current != null) {
        root.current.render(
          DesignStudioReact.createElement(DesignStudioApp, {
            basename: props?.basename,
            plumaContextValue: plumaContextValueStore,
            plumaTheme: plumaThemeStore,
            plumaColorScheme: plumaColorSchemeStore,
            plumaColorSchemePreference: plumaColorSchemePreferenceStore,
            plumaThemes: plumaThemesStore,
          }) as React.ReactElement,
        );
        rendered.current = true;
        recordRumMilestone(RumMilestone.ChildAppRendered);
      }

      return () => {
        if (rendered.current != null) {
          root.current?.unmount();
          root.current = null;
          rendered.current = false;
        }
      };
    },
    [props?.basename],
  );

  return (
    <AppBundleLoader
      appBundle={AppBundle.DesignStudio}
      className="design-studio-app"
      renderApp={renderDesignStudioApp}
    />
  );
}
